import { Fragment, memo, useMemo, type ReactNode } from "react";

import { SummaryText } from "@/app/app/_/views/RulesView/nodes/SummaryText";
import { mapServerSummaryParts } from "@/app/app/_/views/RulesView/utils/nodeSummary";
import { MessageContextObject } from "@/__generated__/graphql";
import { ToolCallCard } from "@/components/organisms/ToolCallCard";
import {
  toToolCallContent,
  renderInlineToolRow,
  buildContentTree,
  getRuleStepMessageText,
  getRuleStepSummaryItem,
  hasActiveExploreInTree,
  isGatedInternalQuery,
  isQueryToolActive,
  isQueryToolCall,
  isThinkToolActive,
  isThinkToolCall,
  shouldRenderWorkflowStepSummaryParts,
  type StreamContext,
  type UnifiedContentItem,
  workflowStepForResults,
} from "@/components/organisms/ToolCallCard/ToolCallCard.tools";
import {
  TextWithReferences,
  TypedDataTable,
} from "@/components/organisms/ToolCallCard/ToolCallCardRichText";
import { PendingPlanningRow } from "@/components/organisms/ToolCallCard/ToolCallCardPendingPlanningRow";
import {
  MessageContext,
  StreamContextsDisplay,
} from "@/components/organisms/ToolCallCard/ToolCallCardMessageContexts";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

// helpers

export function getSenderLabel(
  account: { id: string; identifier: string } | null | undefined,
  content: UnifiedContentItem[],
): string {
  if (account != null) return "YOU";
  const nodeEval = content.find((c) => c.type === "node_eval");
  if (!nodeEval) return "AGENT";
  const name =
    nodeEval.toolName?.trim() || nodeEval.ruleNode?.name?.trim() || "node";
  if (nodeEval.ruleNode?.__typename === "RuleNodeActionThreadObject") {
    return `Agent (${name})`;
  }
  const hasSummary =
    !!getRuleStepMessageText(nodeEval) ||
    (nodeEval.ruleNode?.summaryParts?.length ?? 0) > 0;
  return hasSummary ? "AGENT" : `Workflow (${name})`;
}

function formatToolNameSimple(name: string): string {
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function useShouldShowPendingPlanning(
  isStreaming: boolean,
  content: UnifiedContentItem[],
) {
  return useMemo(() => {
    if (!isStreaming) return false;

    const visible = content;
    if (visible.length === 0) return true;
    if (hasActiveExploreInTree(content)) return false;

    // Keep planning until a visible row actually occupies the stream (avoids a blank gap
    // when cache rows exist but ContentRow still returns null, e.g. empty streaming text).
    const hasStreamSurface = visible.some((item) => {
      if (item.type === "text") return !!item.text?.trim();
      if (item.type === "tool_call" || item.type === "node_eval") return true;
      return false;
    });
    if (!hasStreamSurface) return true;

    const lastItem = content[content.length - 1];
    if (isThinkToolActive(lastItem) || isQueryToolActive(lastItem))
      return false;
    if (
      lastItem.type === "text" &&
      lastItem.isStreaming &&
      !lastItem.text?.trim()
    ) {
      return true;
    }
    if (lastItem.type === "tool_call") {
      return !!(lastItem.toolOutput || lastItem.toolError);
    }
    return false;
  }, [isStreaming, content]);
}

// ContentRow

type ContentRowProps = {
  item: UnifiedContentItem;
  index: number;
  isAgent: boolean;
  autofocus?: boolean;
  threadIsStreaming?: boolean;
  siblings?: UnifiedContentItem[];
  /** How long the turn thought before this row, when it stands for its head. */
  thoughtSeconds?: number | null;
};

function ContentRowInner({
  item,
  index,
  isAgent,
  autofocus = false,
  threadIsStreaming = false,
  siblings = [],
  thoughtSeconds,
}: ContentRowProps) {
  const contentType = item.type || "text";
  const itemKey = item.id || item.identifier || index;

  const inline = renderInlineToolRow({ item, thoughtSeconds });
  if (inline) return inline;
  if (contentType === "tool_call" || contentType === "node_eval") {
    return (
      <ToolCallCard
        item={toToolCallContent(item, index)}
        threadIsStreaming={threadIsStreaming}
      />
    );
  }
  if (contentType === "tool_result") {
    const nodes = item.nodes;
    if (nodes?.length) {
      const typename = (nodes[0] as { __typename?: string }).__typename;
      return (
        <div className="mt-2 mb-2 w-full">
          <TypedDataTable items={nodes} typename={typename ?? "Unknown"} />
        </div>
      );
    }
    return (
      <div
        className={cn(
          "inline-flex items-center gap-1 pl-3 pr-3 pt-1 pb-1 rounded-xl",
          item.toolError ? "bg-destructive/10" : "bg-secondary",
          item.toolError ? "text-destructive" : "text-foreground",
          "text-[11px] font-medium",
        )}
      >
        {formatToolNameSimple(item.toolName || "unknown")}
      </div>
    );
  }
  if (contentType === "pending_change") {
    return (
      <div className="mt-2 mb-2 p-2 rounded-sm bg-secondary [border-width:1px] [border-style:solid] border-border text-xs">
        <div className="flex items-center gap-2">
          <div className="text-[11px]">⏳</div>
          <Typography level="body-sm" className="text-foreground">
            Pending change
          </Typography>
        </div>
      </div>
    );
  }

  // A note about the turn itself — a reply the user stopped, say. Set apart
  // from the agent's own words rather than reading as something it said.
  if (contentType === "notice") {
    return (
      <Typography level="body-sm" className="text-muted-foreground/80 italic">
        {item.text}
      </Typography>
    );
  }

  const elements: ReactNode[] = [];

  if (item.text) {
    if (isAgent) {
      elements.push(
        <div
          key={`${itemKey}-text`}
          className="block w-full max-w-full min-w-0"
        >
          {item.isStreaming ? (
            <div className="block w-full max-w-full min-w-0">
              <TextWithReferences text={item.text} />
            </div>
          ) : (
            <TextWithReferences text={item.text} />
          )}
        </div>,
      );
    } else {
      elements.push(
        <Typography
          component="span"
          level="body-md"
          key={`${itemKey}-text`}
          className="text-foreground inline"
        >
          {item.text}
        </Typography>,
      );
    }
  }

  if (item.context?.length) {
    const firstCtx = item.context[0] as {
      isPresented?: boolean;
      node?: unknown;
    };
    if (typeof firstCtx?.isPresented === "boolean") {
      (item.context as MessageContextObject[]).forEach((ctx, ctxIndex) => {
        elements.push(
          <MessageContext
            key={`${item.id || index}-context-${ctxIndex}`}
            context={ctx}
            isAgent={isAgent}
            autofocus={autofocus}
          />,
        );
      });
    } else if (firstCtx?.node) {
      elements.push(
        <StreamContextsDisplay
          key={`${itemKey}-ctx`}
          contexts={item.context as StreamContext[]}
          autofocus={autofocus}
        />,
      );
    }
  }

  if (!elements.length) return null;
  if (!isAgent) {
    return (
      <div key={itemKey} className="inline flex-wrap items-center gap-1">
        {elements}
      </div>
    );
  }
  return <Fragment key={itemKey}>{elements}</Fragment>;
}

/**
 * Whether a row is showing the same thing it was, the rows under it included.
 *
 * A call's own rows arrive while the call is still running — one per file it is
 * working through — and nothing else about the call changes as they do. So the
 * rows under a row are part of what it is showing, compared row by row rather
 * than by the array holding them: the tree is rebuilt from the message's
 * content on every frame, so that array is never the same object twice.
 */
function isShowingTheSame(
  prev: UnifiedContentItem,
  next: UnifiedContentItem,
): boolean {
  if (
    prev.id !== next.id ||
    prev.identifier !== next.identifier ||
    prev.text !== next.text ||
    prev.isStreaming !== next.isStreaming ||
    prev.type !== next.type ||
    prev.status !== next.status ||
    prev.toolName !== next.toolName ||
    prev.toolInput !== next.toolInput ||
    prev.toolOutput !== next.toolOutput ||
    prev.toolError !== next.toolError
  )
    return false;
  const prevChildren = prev.children ?? [];
  const nextChildren = next.children ?? [];
  return (
    prevChildren.length === nextChildren.length &&
    prevChildren.every((child, index) =>
      isShowingTheSame(child, nextChildren[index]),
    )
  );
}

const ContentRow = memo(
  ContentRowInner,
  (prev, next) =>
    prev.index === next.index &&
    prev.isAgent === next.isAgent &&
    prev.autofocus === next.autofocus &&
    prev.threadIsStreaming === next.threadIsStreaming &&
    prev.thoughtSeconds === next.thoughtSeconds &&
    isShowingTheSame(prev.item, next.item),
);

// ThreadMessageBody — content only; label + shell live on ThreadTurnPrompt

export type ThreadMessageBodyProps = {
  content: UnifiedContentItem[];
  account?: { id: string; identifier: string } | null;
  isStreaming?: boolean;
  autofocus?: boolean;
  turnIndex?: number;
  /** `prompt` — workflow summary only; `tail` — tools below the bubble; `full` — entire body. */
  part?: "prompt" | "tail" | "full";
  /** When the turn began: the reply's own ``createdAt``, made as it was sent. */
  startedAt?: string | null;
};

function ThreadMessageBodyInner({
  content,
  account,
  isStreaming = false,
  autofocus = false,
  turnIndex,
  part = "full",
  startedAt,
}: ThreadMessageBodyProps) {
  const isAgent = account == null;
  const rawWorkflowStep = getRuleStepSummaryItem(content);
  const workflowStep =
    rawWorkflowStep && (rawWorkflowStep.type === "node_eval" || account != null)
      ? rawWorkflowStep
      : null;
  const shouldShowPendingPlanning = useShouldShowPendingPlanning(
    isStreaming,
    content,
  );
  // A tool call the agent got wrong and went on from - an input it could not
  // read, say, told to it to call again - was its own to put right: it is not
  // shown. One its turn ended on may be why it stopped, and is.
  const visibleContent = useMemo(
    () =>
      content.filter(
        (item, index) =>
          !(
            item.type === "tool_call" &&
            item.toolError &&
            index < content.length - 1
          ),
      ),
    [content],
  );

  const rows = (items: UnifiedContentItem[]) =>
    items.map((item, index) => (
      <ContentRow
        key={item.id || item.identifier || index}
        item={item}
        index={index}
        isAgent={isAgent}
        autofocus={autofocus}
        threadIsStreaming={isStreaming}
        siblings={items}
        thoughtSeconds={item === mergedThought ? thoughtSeconds : undefined}
      />
    ));

  // The head of an agent's reply: "Planning next moves" until something shows,
  // then how long that took — worked out here from when the turn began and
  // when its first row was made, which the server stamps as that row's first
  // content arrives. It stays once the turn is over. A note about the turn
  // (a stop) is not something the agent showed.
  //
  // A thought is part of what is being timed, not the end of it: its row is
  // made as its first word arrives, so timed to there the number was fixed a
  // few seconds in while the agent was still thinking. The clock runs to the
  // first row that is not a thought, and until there is one there is no
  // number to show.
  const shown = content.filter((item) => item.type !== "notice");
  const firstShownAt = shown.reduce<number | null>((earliest, item) => {
    if (isThinkToolCall(item)) return earliest;
    const at = item.createdAt ? Date.parse(item.createdAt) : NaN;
    if (Number.isNaN(at)) return earliest;
    return earliest == null || at < earliest ? at : earliest;
  }, null);
  const began = startedAt ? Date.parse(startedAt) : NaN;
  const thoughtSeconds =
    firstShownAt != null && !Number.isNaN(began)
      ? Math.max(0, Math.round((firstShownAt - began) / 1000))
      : null;
  // A reply that opens on a thought says so twice — the head above the row and
  // the row itself, both "Thought". The row takes the head's time and the head
  // is left off, so one thought reads as one.
  const mergedThought =
    isAgent && shown.length > 0 && isThinkToolCall(shown[0]) ? shown[0] : null;
  const turnHead = !isAgent ? null : shown.length === 0 ? (
    isStreaming ? (
      <PendingPlanningRow />
    ) : null
  ) : mergedThought ? null : (
    <PendingPlanningRow thoughtSeconds={thoughtSeconds} />
  );

  if (workflowStep && part !== "full") {
    const isNodeEvalStep = workflowStep.type === "node_eval";
    const tailItems = isNodeEvalStep
      ? []
      : visibleContent.filter((item) => item !== workflowStep);
    const useSummaryParts = shouldRenderWorkflowStepSummaryParts(workflowStep);
    const summaryParts = useSummaryParts
      ? mapServerSummaryParts(workflowStep.ruleNode?.summaryParts ?? null)
      : [];
    const summaryPlainText = getRuleStepMessageText(workflowStep);

    if (part === "prompt") {
      if (summaryParts.length > 0) {
        return (
          <Typography
            component="div"
            level="body-md"
            className="text-foreground [line-height:1.35]"
          >
            <SummaryText parts={summaryParts} />
          </Typography>
        );
      }
      if (summaryPlainText) {
        return (
          <Typography
            component="div"
            level="body-md"
            className="text-foreground"
          >
            <TextWithReferences text={summaryPlainText} />
          </Typography>
        );
      }
      return null;
    }

    const hasTail =
      isNodeEvalStep || tailItems.length > 0 || shouldShowPendingPlanning;
    if (!hasTail) return null;

    return (
      <div className="flex flex-col gap-2 text-[var(--muted-foreground)_!important] w-full max-w-full min-h-0 min-w-0">
        {isNodeEvalStep ? (
          <ContentRow
            key={
              workflowStep.id ||
              workflowStep.identifier ||
              "workflow-step-results"
            }
            item={workflowStepForResults(workflowStep)}
            index={0}
            isAgent={isAgent}
            autofocus={autofocus}
            threadIsStreaming={isStreaming}
          />
        ) : (
          rows(tailItems)
        )}
        {shouldShowPendingPlanning && <PendingPlanningRow />}
      </div>
    );
  }

  if (workflowStep && part === "full") {
    const isNodeEvalStep = workflowStep.type === "node_eval";
    const tailItems = isNodeEvalStep
      ? []
      : visibleContent.filter((item) => item !== workflowStep);
    const useSummaryParts = shouldRenderWorkflowStepSummaryParts(workflowStep);
    const summaryParts = useSummaryParts
      ? mapServerSummaryParts(workflowStep.ruleNode?.summaryParts ?? null)
      : [];
    const summaryPlainText = getRuleStepMessageText(workflowStep);

    const summaryNode =
      summaryParts.length > 0 ? (
        <Typography
          component="div"
          level="body-md"
          className="text-foreground [line-height:1.35]"
        >
          <SummaryText parts={summaryParts} />
        </Typography>
      ) : summaryPlainText ? (
        <Typography component="div" level="body-md" className="text-foreground">
          <TextWithReferences text={summaryPlainText} />
        </Typography>
      ) : null;

    const hasTail =
      isNodeEvalStep || tailItems.length > 0 || shouldShowPendingPlanning;
    if (!summaryNode && !hasTail) return null;

    return (
      <div className="flex flex-col gap-2 text-[var(--muted-foreground)_!important] w-full max-w-full min-h-0 min-w-0">
        {summaryNode}
        {isNodeEvalStep ? (
          <ContentRow
            key={
              workflowStep.id ||
              workflowStep.identifier ||
              "workflow-step-results"
            }
            item={workflowStepForResults(workflowStep)}
            index={0}
            isAgent={isAgent}
            autofocus={autofocus}
            threadIsStreaming={isStreaming}
          />
        ) : (
          rows(tailItems)
        )}
        {shouldShowPendingPlanning && <PendingPlanningRow />}
      </div>
    );
  }

  if (part === "tail") return null;

  return (
    <div className="flex flex-col gap-2 text-[var(--muted-foreground)_!important] w-full max-w-full min-h-0 min-w-0">
      {turnHead}
      {rows(visibleContent)}
    </div>
  );
}

export const ThreadMessageBody = memo(
  ThreadMessageBodyInner,
  (prev, next) =>
    prev.content === next.content &&
    prev.isStreaming === next.isStreaming &&
    prev.autofocus === next.autofocus &&
    prev.turnIndex === next.turnIndex &&
    prev.part === next.part &&
    prev.startedAt === next.startedAt &&
    prev.account?.id === next.account?.id,
);
